import { Popper } from '@mui/base';
import { ClickAwayListener } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import useAnalyticsTracker from '../../../../analytics/hooks/useAnalyticsTracker';
import styles from './AppbarItemWithMenu.module.scss';

export interface AppbarItemWithMenuProps<T> {
  open: boolean;
  setOpen: (open: boolean) => void;
  Item: React.FC<T>;
  itemProps: T;
  hoverTrackingAction: string;
  hoverTrackingProps?: unknown;
  children: ReactNode;
}

export function AppbarItemWithMenu<T>({
  open,
  setOpen,
  Item,
  itemProps,
  hoverTrackingAction,
  hoverTrackingProps,
  children
}: AppbarItemWithMenuProps<T>) {
  const { eventTrack } = useAnalyticsTracker();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [isAnchorHovered, setAnchorHovered] = useState(false);
  const [isPopperHovered, setPopperHovered] = useState(false);
  const [openIntent, setOpenIntent] = useState<string | null>(null);

  useEffect(() => {
    if (!isAnchorHovered && !isPopperHovered && openIntent === 'hover' && open) {
      const timer = setTimeout(() => {
        setOpen(false);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isAnchorHovered, isPopperHovered]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    let shouldOpen = !open;

    if (openIntent === 'hover' && open) {
      shouldOpen = true;
    }

    if (shouldOpen) {
      setAnchorEl(event.currentTarget);
      setOpenIntent('click');
    } else {
      setOpenIntent(null);
    }

    setOpen(shouldOpen);
  };

  return (
    <>
      <div
        className='w-100'
        onClick={handleClick}
        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
          setAnchorHovered(true);
          if (!open) {
            setOpenIntent('hover');
            setAnchorEl(event.currentTarget);
            setOpen(true);
          }

          eventTrack({
            action: hoverTrackingAction,
            properties: hoverTrackingProps
          });
        }}
        onMouseLeave={() => setAnchorHovered(false)}
      >
        <Item {...itemProps} />
      </div>

      <Popper
        open={open}
        anchorEl={anchorEl}
        className={styles.appbarMenuContainer}
        placement='right'
      >
        <div
          onMouseEnter={() => setPopperHovered(true)}
          onMouseLeave={() => setPopperHovered(false)}
        >
          <ClickAwayListener
            onClickAway={event => {
              if (!anchorEl.contains(event.target as any)) {
                setOpen(false);
              }
            }}
          >
            <div className={styles.appbarMenu}>{children}</div>
          </ClickAwayListener>
        </div>
      </Popper>
    </>
  );
}
